import styled from 'styled-components'
import React, { ComponentProps, ReactNode, useRef } from 'react'

import { theme } from 'styles/theme'
import { DotsGhostButton, PrimaryMenuButton, SecondaryMenuButton, type ButtonProps } from '../Button/Button'
import { ChevronIcon, SvgType } from '../Icon/Icon'
import { Row } from '../Table/Table'
import useToggle from 'hooks/useToggle'

const Details = styled.details`
  display: inline-flex;
  text-align: start;
  position: relative;
  user-select: none;
  ${ChevronIcon} {
    transition: transform ${theme.transitions.normal} ease-out;
    margin-inline-start: 0;
  }
  &[open] ${ChevronIcon} {
    transform: rotate(-180deg);
  }
`

const Summary = styled.summary<WithIsDisabled>`
  display: inline-flex;
  align-items: center;
  gap: 0.2em;
  transition: all ${theme.transitions.normal} ease-out;
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'all')};
  ::-webkit-details-marker {
    display: none;
  }
  :hover {
    color: ${theme.colors.bodyHover};
  }
`

const MenuGroupList = styled.ul`
  white-space: nowrap;
`

const Menu = styled(MenuGroupList)`
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.body};
  background-color: ${theme.colors.bodyBg};
  position: absolute;
  border-radius: ${theme.radii.small};
  border: ${theme.borders.menu};
  box-shadow: ${theme.shadows.menu};
  text-transform: none;
  z-index: 20;
  min-width: 12em;
`

const MenuGroupItem = styled.li`
  :not(:last-child) {
    border-block-end: ${theme.borders.menuSeparator};
  }
`

const MenuLink = styled.a<WithIsSelected>`
  padding: 1em;
  display: block;
  inline-size: 100%;
  border-radius: ${theme.radii.small};
  transition: all ${theme.transitions.normal} ease-out;
  color: ${({ isSelected }) => (isSelected ? theme.colors.GreenDarkest : 'currentColor')};
  background-color: ${({ isSelected }) => (isSelected ? theme.colors.Grey1 : 'inherit')};
  :hover,
  :focus {
    background-color: ${theme.colors.Grey1};
  }
`

const MenuListItem = styled.li<MaybeWithIsDisabled & WithIsSelected>`
  color: ${({ isDisabled }) => (isDisabled ? theme.colors.Gray8 : 'inherit')};
  > ${MenuLink} {
    pointer-events: ${({ isDisabled, isSelected }) => (isDisabled || isSelected ? 'none' : 'all')};
  }
`

interface MenuItem extends MaybeWithIsDisabled, MaybeWithTitle, WithText, MaybeWithIsSelected {
  onClick?: VoidFunction
  name?: string
  Icon?: SvgType
}

interface MenuItemAnchor extends MenuItem, WithHref {}
interface MenuItemButton extends MenuItem {
  href?: undefined
}

type MenuButtonItem = MenuItemAnchor | MenuItemButton

export type MenuButtonGroups = ReadonlyArray<ReadonlyArray<MenuButtonItem>>

interface MenuButtonContentProps {
  menuButtonGroups: MenuButtonGroups
}

type MenuDirection = 'start' | 'end'
interface MenuExpansionDirection {
  block?: MenuDirection
  inline?: MenuDirection
}

interface MenuButtonProps extends ComponentProps<typeof Details>, MenuButtonContentProps, MaybeWithIsDisabled {
  isOpen?: boolean
  ButtonComponent: ReactNode
  menuStyle?: WithStyle['style']
  menuExpansionDirection?: MenuExpansionDirection
}

type DropdownProps = {
  children: ReactNode
  ButtonComponent: ReactNode,
  isDisabled?: boolean,
  closeOnClickInside?: boolean,
  initiallyOpen?: boolean,
  onBlur?: (e: React.FocusEvent) => void,
}

export const Dropdown: React.FC<DropdownProps> = ({
  ButtonComponent,
  children,
  closeOnClickInside = true,
  isDisabled = false,
  initiallyOpen = false,
  onBlur,
  ...rest
}) => {
  const [isOpen, toggle] = useToggle(initiallyOpen)

  const ref = useRef<HTMLDivElement>(null)

  return (
    // The "preventDefault" stuff is due to https://github.com/facebook/react/issues/15486
    <Details
      {...rest}
      open={isOpen}
      onBlur={(e: React.FocusEvent) => {
        if (!closeOnClickInside && ref.current == e.relatedTarget) {
          return
        }

        onBlur?.(e)
        toggle(false)
      }}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        toggle(true)
        e.stopPropagation()
      }}
    >
      <Summary
        isDisabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0 }
      >
        {ButtonComponent}
      </Summary>
      <div
        ref={ref}
        tabIndex={0}
      >
        {children}
      </div>
    </Details>
  )
}

export function MenuButton({
  menuButtonGroups,
  ButtonComponent,
  menuExpansionDirection = { inline: 'end', block: 'end' },
  menuStyle,
  isDisabled = false,
  isOpen = false,
  ...props
}: MenuButtonProps) {
  const menuAlignmentStyle: WithStyle['style'] = {
    insetInline: menuExpansionDirection.inline === 'end' ? 'auto 0' : '0 auto',
    insetBlock: menuExpansionDirection.block === 'end' ? '100% auto' : 'auto 100%',
    ...menuStyle,
  } as const

  return (
    <Dropdown
      isDisabled={isDisabled}
      ButtonComponent={ButtonComponent}
      initiallyOpen={isOpen}
      {...props}
    >
      {/* tabIndex allows the disabled state, which details/summary lacks: https://stackoverflow.com/a/13605221/915875 */}
      <Menu {...{ style: menuAlignmentStyle }}>
        {menuButtonGroups.map((group, key) => (
          <MenuGroupItem {...{ key }}>
            <MenuGroupList>
              {group.map(({ text, href, onClick: onMenuItemClick, isDisabled = false, title, isSelected = false, Icon }) => (
                <MenuListItem {...{ key: text, isDisabled, ...(Boolean(title) && { title }), isSelected }}>
                  <MenuLink
                    {...{
                      ...(href === undefined ? { as: 'button', type: 'button', disabled: isDisabled } : { href }),
                      ...(onMenuItemClick && { onClick: (e: React.MouseEvent) => { onMenuItemClick(); e.stopPropagation() }}),
                      tabIndex: isDisabled ? -1 : 0,
                      isSelected,
                      onMouseDown: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
                        e.preventDefault()
                      }, // https://stackoverflow.com/a/57630197/4574731
                    }}
                  >
                    {Icon ? (
                      <MenuLinkContainer>
                        <Icon/>
                        <span>{text}</span>
                      </MenuLinkContainer>
                    ) : (
                      text
                    )}
                  </MenuLink>
                </MenuListItem>
              ))}
            </MenuGroupList>
          </MenuGroupItem>
        ))}
      </Menu>
    </Dropdown>
  )
}

type DotsMenuButtonProps = MenuButtonContentProps & ButtonProps

export const DotsMenuButton: React.FC<DotsMenuButtonProps> = ({
  text,
  isDisabled = false,
  onClick,
  ...rest
}) => {
  return (
    <MenuButton
      ButtonComponent={<DotsGhostButton {...{ tagName: 'span', text, isDisabled, onClick }} />}
      isDisabled={isDisabled}
      {...rest}
    />
  )
}

const MenuLinkContainer = styled(Row)`
  justify-content: space-between;
  align-items: center;
  gap: 1em;
  float: left;
`

export type DotsMenuButtonType = typeof DotsMenuButton

type PrimaryDropdownMenuButtonProps = MenuButtonContentProps & ButtonProps

const StyledPrimaryMenuButton = styled(PrimaryMenuButton)`
  cursor: default;
`

export function PrimaryDropdownMenuButton({ text, isDisabled = false, ...props }: PrimaryDropdownMenuButtonProps) {
  // We use a span tag for the button cause otherwise details/summary doesn't play nice with a nested button.
  return (
    <MenuButton
      {...{
        ButtonComponent: <StyledPrimaryMenuButton {...{ tagName: 'span', text, isDisabled }} />,
        isDisabled,
        ...props,
      }}
    />
  )
}

export type PrimaryDropdownMenuButtonType = typeof PrimaryDropdownMenuButton

type SecondaryDropdownMenuButtonProps = PrimaryDropdownMenuButtonProps

export function SecondaryDropdownMenuButton({ text, isDisabled = false, ...props }: SecondaryDropdownMenuButtonProps) {
  // We use a span tag for the button cause otherwise details/summary doesn't play nice with a nested button.
  return (
    <MenuButton
      {...{ ButtonComponent: <SecondaryMenuButton {...{ tagName: 'span', text, isDisabled }} />, isDisabled, ...props }}
    />
  )
}

export type SecondaryDropdownMenuButtonType = typeof SecondaryDropdownMenuButton
